import { useCallback, useEffect, useState } from 'react';
import { Flex, UploadFile } from 'antd';
import classNames from 'classnames';
import Upload, { UploadProps } from 'antd/es/upload/Upload';
import { ReactComponent as AttachmentIcon } from '@Icons/additional/attachment.svg';
import { ReactComponent as DownloadIcon } from '@Icons/additional/download.svg';
import { ReactComponent as RemoveIcon } from '@Icons/additional/trash.svg';
import { RcCustomRequestOptions } from '@Components/types/UploadType';
import { Attachment } from '@Components/types/Attachment';
import { useDownloadDocumentQuery } from '@Store/api/dmsApiSlice';

import styles from './AttachmentButton.module.css';

interface AttachmentButtonProps {
  uploadedFile: Attachment | undefined;
  onFileRequest: (options: RcCustomRequestOptions) => void;
  onFileRemove: (value: boolean | undefined) => void;
}

type OriginalFunction = UploadProps['itemRender'];

type ExtendFunction<F, P> = F extends (
  params: infer OriginalParams
) => infer ReturnType
  ? (params: P) => ReturnType
  : (params: P) => JSX.Element;

type ExtendedParams = {
  file: UploadFile;
  showUploadIcon: boolean;
  actions: { download: () => void; preview: () => void; remove: () => void };
};

type ModifiedFunction = ExtendFunction<OriginalFunction, ExtendedParams>;

const renderFileItem: ModifiedFunction = ({
  file,
  showUploadIcon,
  actions,
}) => {
  return (
    <div className={styles.containerFileUpload}>
      <div className={styles.fileName} title={file?.name}>
        <AttachmentIcon /> {file?.name}
      </div>
      <Flex gap={6}>
        {showUploadIcon && (
          <DownloadIcon className={styles.cursor} onClick={actions.download} />
        )}
        <RemoveIcon className={styles.cursor} onClick={actions.remove} />
      </Flex>
    </div>
  );
};

const AttachmentButton: React.FC<AttachmentButtonProps> = ({
  uploadedFile,
  onFileRequest,
  onFileRemove,
}) => {
  const [fileList, setFileList] = useState(
    // eslint-disable-next-line
    [] as UploadFile<any>[]
  );
  const { data: imageURL } = useDownloadDocumentQuery(uploadedFile?.id || '', {
    skip: !uploadedFile?.id,
  });

  const onDownloadClick = useCallback((): void => {
    if (imageURL && uploadedFile) {
      const a = document.createElement('a');
      a.href = imageURL;
      a.download = uploadedFile?.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  }, [imageURL, uploadedFile]);

  useEffect(() => {
    if (uploadedFile) {
      setFileList([
        {
          uid: '-1',
          name: uploadedFile.name,
          status: 'done',
          url: imageURL,
        },
      ]);
    }
  }, [uploadedFile, imageURL]);

  return (
    <div className={styles.containerAttachment}>
      <Upload
        fileList={fileList}
        onChange={({ fileList: fileListNew }) => {
          const fileAdded = !fileList.length && Boolean(fileListNew.length);

          if (fileAdded) {
            onFileRemove(true);
          }

          setFileList(fileListNew);
        }}
        customRequest={onFileRequest}
        onDownload={() => onDownloadClick()}
        onRemove={() => onFileRemove(Boolean(uploadedFile))}
        itemRender={(__, file, _, actions) =>
          renderFileItem({
            file,
            actions,
            showUploadIcon: Boolean(imageURL),
          })
        }
        maxCount={1}
      >
        <Flex
          className={classNames(
            styles['header-btn'],
            'header-action-component trojan-neumorphism-box trojan-neumorphism-box-shadow'
          )}
          onClick={() => {
            console.error('Not implemented');
          }}
          gap="small"
        >
          <AttachmentIcon />
        </Flex>
      </Upload>
    </div>
  );
};

export default AttachmentButton;
